import { useQueryClient } from '@tanstack/react-query'
import type { BookingNote } from '@transreport/pauk-rrms-api'
import {
  getGetBookingNotesQueryKey,
  getShowAssistanceChecklistQueryOptions,
  useCreateBookingNote,
  useGetBookingNotes,
  useShowAssistanceChecklist,
} from '@transreport/pauk-rrms-api'
import { AlertCircle, Clock } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppButton, Modal } from '@/components/framework'
import { AddAssistanceNoteModal } from '@/components/shared/AddAssistanceNoteModal'
import { CompleteChecklistModal } from '@/components/shared/CompleteChecklistModal'
import { EmailManagerModal } from '@/components/shared/EmailManagerModal'
import { LinkedJobsContext } from '@/components/shared/LinkedJobsContext'
import { ValidationReminderModal } from '@/components/shared/ValidationReminderModal'
import { getBookingUrl } from '@/lib/bookingLinks'
import { getRealtimeSeverity } from '@/lib/timeline/realtimeClassification'
import type { CallsignRow, Request } from '@/types/timeline'

interface AssistanceDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  request: Request | null
  callsigns: CallsignRow[]
  onAssignClick?: () => void
  stationCode: string
}

export const AssistanceDetailsModal: React.FC<AssistanceDetailsModalProps> = ({
  isOpen,
  onClose,
  request,
  callsigns,
  onAssignClick,
  stationCode,
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false)
  const [isChecklistOpen, setIsChecklistOpen] = useState(false)
  const [isEmailManagerOpen, setIsEmailManagerOpen] = useState(false)
  const [isValidationReminderOpen, setIsValidationReminderOpen] = useState(false)

  const prefetchChecklist = useCallback(() => {
    if (!request?.id) return

    void queryClient.prefetchQuery(
      getShowAssistanceChecklistQueryOptions(request.id, {
        query: { staleTime: 60_000 },
      })
    )
  }, [request?.id, queryClient])

  useEffect(() => {
    if (!isOpen) return
    prefetchChecklist()
  }, [isOpen, prefetchChecklist])

  // Always compute bookingId and set up the mutation so hooks are called
  // in a consistent order, even when no request is selected.
  const bookingId = request?.bookingId ?? ''

  const checklistStatusQuery = useShowAssistanceChecklist(request?.id ?? '', {
    query: {
      enabled: isOpen && Boolean(request?.id),
      staleTime: 60_000,
    },
  })

  const bookingLogQuery = useGetBookingNotes(
    bookingId,
    { station_code: stationCode },
    {
      query: {
        enabled: isAddNoteOpen && Boolean(bookingId),
        staleTime: 60_000,
      },
    }
  )

  const isChecklistComplete = checklistStatusQuery.data?.completed ?? false

  const createBookingNoteMutation = useCreateBookingNote({
    mutation: {
      meta: {
        analytics: {
          event: 'note_added',
          area: 'timeline',
          parameters: () => ({ station_code: stationCode }),
        },
        toast: {
          success: t('assistanceNotes.saveSuccess'),
          error: t('assistanceNotes.saveError'),
        },
      },
      onMutate: async (variables) => {
        if (!variables?.id) return
        const key = getGetBookingNotesQueryKey(variables.id, { station_code: stationCode })
        await queryClient.cancelQueries({ queryKey: key })

        const previous = queryClient.getQueryData<BookingNote[]>(key) ?? []
        const optimistic: BookingNote = {
          id: `optimistic-${Date.now()}`,
          text: variables.data?.text ?? '',
          created_at: new Date().toISOString(),
          created_by: undefined,
        }
        queryClient.setQueryData<BookingNote[]>(key, [optimistic, ...previous])

        return { key, previous }
      },
      onError: (_error, _variables, context) => {
        if (!context?.key) return
        queryClient.setQueryData(context.key, context.previous)
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetBookingNotesQueryKey(bookingId, { station_code: stationCode }),
        })
      },
      onSettled: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetBookingNotesQueryKey(bookingId, { station_code: stationCode }),
        })
      },
    },
  })

  if (!request) return null

  const currentCallsign = callsigns.find((c) => c.requests.some((r) => r.id === request.id))

  const paRef = request.ref ?? ''
  const bookingUrl = getBookingUrl(paRef)
  const passengerName = request.customerName ?? t('assistanceDetails.notAvailable')
  const journeyText = request.additionalInfo ?? t('assistanceDetails.notAvailable')
  const coach = t('assistanceDetails.notAvailable')
  const platform = request.platform ?? t('assistanceDetails.notAvailable')
  const assistanceSummary = request.assistanceDetails ?? t('assistanceDetails.notAvailable')

  const severity = getRealtimeSeverity(request.realtimeClassification)
  const hasRealtimeAlert = severity !== 'normal'
  const RealtimeIcon = severity === 'delayed' ? Clock : AlertCircle
  const realtimeIconClass = severity === 'delayed' ? 'text-warning-text' : 'text-danger'
  const classificationLabel = request.realtimeClassification
    ? t(`realtimeClassification.${request.realtimeClassification}`, { defaultValue: '' })
    : ''

  const stationLabel =
    request.type === 'ARR'
      ? (request.boardStationName ?? request.boardStationCode)
      : (request.alightStationName ?? request.alightStationCode)

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('assistanceDetails.title')} size="lg">
      <div className="space-y-6">
        {stationLabel && (
          <div className="flex items-center gap-2">
            <span
              className={`rounded border px-2 py-0.5 text-xs font-bold tracking-wide uppercase ${
                request.type === 'ARR'
                  ? 'border-brand bg-arrival-bg text-arrival-text'
                  : 'border-amber-600 bg-departure-bg text-departure-text'
              }`}
            >
              {request.type}
            </span>
            <span className="text-base font-semibold text-gray-900">{stationLabel}</span>
          </div>
        )}
        <section aria-labelledby="journey-info-heading">
          <h2 id="journey-info-heading" className="mb-3 text-base font-semibold text-gray-900">
            {t('assistanceDetails.journeyInformation')}
          </h2>

          <div className="grid grid-cols-2 gap-4 text-sm text-gray-900">
            <div>
              <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                {t('assistanceDetails.paRef')}
              </div>
              <div>
                {bookingUrl ? (
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-primary underline"
                  >
                    {paRef}
                  </a>
                ) : (
                  paRef
                )}
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                {t('assistanceDetails.passengerName')}
              </div>
              <div>{passengerName}</div>
            </div>

            <div>
              <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                {t('assistanceDetails.headcode')}
              </div>
              <div>{request.headCode ?? t('assistanceDetails.notAvailable')}</div>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                {t('assistanceDetails.toc')}
              </div>
              <div>{t('assistanceDetails.notAvailable')}</div>
            </div>

            <div>
              <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                {t('assistanceDetails.departure')}
              </div>
              <div className="space-y-0.5">
                <span
                  className={`font-medium ${hasRealtimeAlert && request.realtimeDeparture ? 'text-gray-600' : ''}`}
                >
                  {request.scheduledDeparture ?? t('assistanceDetails.notAvailable')}
                </span>
                {hasRealtimeAlert && request.realtimeDeparture && (
                  <div className={`flex items-center gap-1 text-sm font-bold ${realtimeIconClass}`}>
                    <RealtimeIcon size={13} aria-hidden="true" />
                    <span>{request.realtimeDeparture}</span>
                    {classificationLabel && (
                      <span className="text-xs font-normal">· {classificationLabel}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                {t('assistanceDetails.arrival')}
              </div>
              <div className="space-y-0.5">
                <span
                  className={`font-medium ${hasRealtimeAlert && request.realtimeArrival ? 'text-gray-600' : ''}`}
                >
                  {request.scheduledArrival ?? t('assistanceDetails.notAvailable')}
                </span>
                {hasRealtimeAlert && request.realtimeArrival && (
                  <div className={`flex items-center gap-1 text-sm font-bold ${realtimeIconClass}`}>
                    <RealtimeIcon size={13} aria-hidden="true" />
                    <span>{request.realtimeArrival}</span>
                    {classificationLabel && (
                      <span className="text-xs font-normal">· {classificationLabel}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                {t('assistanceDetails.stations')}
              </div>
              <div>{journeyText}</div>
            </div>
            <div className="space-y-2">
              <div>
                <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                  {t('assistanceDetails.coach')}
                </div>
                <div>{coach}</div>
              </div>
              <div>
                <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                  {t('assistanceDetails.platform')}
                </div>
                <div className="flex items-center gap-1">
                  <span>{platform}</span>
                  {hasRealtimeAlert && request.platform && (
                    <RealtimeIcon size={13} aria-hidden="true" className={realtimeIconClass} />
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase">
                {t('assistanceDetails.callsign')}
              </div>
              <div>{currentCallsign?.name ?? t('assistanceDetails.unassigned')}</div>
            </div>
          </div>
        </section>

        {request.linkedJobContext && (
          <section aria-label={t('linkedJobs.title')}>
            <LinkedJobsContext context={request.linkedJobContext} variant="details" />
          </section>
        )}

        <section aria-labelledby="assistance-types-heading">
          <h2 id="assistance-types-heading" className="mb-3 text-base font-semibold text-gray-900">
            {t('assistanceDetails.assistanceTypes')}
          </h2>

          <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900">
            {assistanceSummary}
          </div>
        </section>

        <div className="mt-4 flex flex-wrap justify-end gap-gap-sm border-t border-border-lighter pt-4">
          <AppButton
            variant="primary"
            size="md"
            onClick={() => {
              if (!isChecklistComplete) {
                setIsValidationReminderOpen(true)
                return
              }
              onAssignClick?.()
            }}
          >
            {t('assistanceDetails.actions.assign')}
          </AppButton>
          <AppButton variant="outline" size="md" onClick={() => setIsAddNoteOpen(true)}>
            {t('assistanceDetails.actions.addNote')}
          </AppButton>
          <AppButton
            variant="outline"
            size="md"
            onClick={() => setIsChecklistOpen(true)}
            onMouseEnter={prefetchChecklist}
            onFocus={prefetchChecklist}
          >
            {isChecklistComplete ? (
              <span className="inline-flex items-center gap-2">
                <span>{t('assistanceDetails.actions.checklistCompleted')}</span>
                <svg
                  className="size-4 text-positive"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3.2-3.2a1 1 0 111.414-1.42l2.493 2.494 6.493-6.494a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            ) : (
              t('assistanceDetails.actions.completeChecklist')
            )}
          </AppButton>
          <AppButton variant="outline" size="md" onClick={() => setIsEmailManagerOpen(true)}>
            {t('assistanceDetails.actions.emailManager')}
          </AppButton>
        </div>

        <AddAssistanceNoteModal
          isOpen={isAddNoteOpen}
          onClose={() => setIsAddNoteOpen(false)}
          bookingLog={bookingLogQuery.data ?? []}
          isBookingLogLoading={bookingLogQuery.isPending}
          bookingLogError={bookingLogQuery.error}
          onAddNote={(text) =>
            createBookingNoteMutation.mutate({
              id: bookingId,
              data: { text },
              params: { station_code: stationCode },
            })
          }
        />

        <CompleteChecklistModal
          area="timeline"
          isOpen={isChecklistOpen}
          onClose={() => setIsChecklistOpen(false)}
          request={request}
        />

        <ValidationReminderModal
          isOpen={isValidationReminderOpen}
          onCancel={() => setIsValidationReminderOpen(false)}
          onContinue={() => {
            setIsValidationReminderOpen(false)
            onAssignClick?.()
          }}
        />

        <EmailManagerModal
          area="timeline"
          isOpen={isEmailManagerOpen}
          onClose={() => setIsEmailManagerOpen(false)}
          assistanceId={request.id}
        />
      </div>
    </Modal>
  )
}
